import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import moment from "moment";
import { truncateToDecimalPlaces } from "../../utils/helper";

const rupeeSymbol = "./rupee-indian.png"
// Register Open Sans font
Font.register({
  family: "Open Sans",
  fonts: [
    { src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf", fontWeight: "normal" },
    { src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-700.ttf", fontWeight: "bold" }
  ]
});

// Define styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
  },
  section: {
    marginBottom: 15,
  },
  heading: {
    fontSize: 12, // Decreased font size
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    backgroundColor: "#0E2841",
    color: "white",
    padding: 6,
    borderRadius: 5,
    marginBottom: 5,
  },
  subHeading: {
    fontSize: 10, // Decreased font size
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    color: "black",
    paddingTop: 2,
    marginBottom: 5,
  },
  subtitle: {
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    fontSize: 8, // Decreased font size
    color: "#6B7280", // Color for the label text
    marginBottom: 15,
  },
  description: {
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "", // Ensures the bold variant is used
    fontSize: 8, // Decreased font size
    color: "#6B7280", // Color for the label text
    marginBottom: 15,
  },
  card: {
    backgroundColor: "#F3F4F6",
    padding: 12, // Adjusted padding
    borderRadius: 8,
    shadowColor: "#000",
    shadowOpacity: 0.3, // Increased shadow opacity
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 5 }, // Increased shadow offset
    marginBottom: 20,
    height: 70, // Fixed height for all cards
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between", // Ensures content is spaced evenly
  },
  cardTitle: {
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    fontSize: 8, // Decreased font size
    color: "#6B7280", // Color for the label text
    marginBottom: 8, // Adjusted margin
  },
  cardValue: {
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    fontSize: 8, // Decreased font size
    marginBottom: 8, // Adjusted margin
  },
  cardDate: {
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    fontSize: 8, // Decreased font size
    color: "#6B7280", // Color for the label text
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  gridItem: {
    width: "24%", // Adjusted width for 3 cards in a row
    marginBottom: 10,
  },
  gridItemPortfolio: {
    width: "24%", // Adjusted width for 4 cards in a row
    marginBottom: 10,
  },
  footer: {
    fontSize: 14, // Decreased font size
    fontWeight: "bold",
    marginTop: 20,
  },
  footerLink: {
    color: "#3B82F6",
    textDecoration: "underline",
  },
  rupeeImage: {
    width: 6,
    height: 6,
    marginHorizontal: 2,
  },
});

// Date formatting function
const formatDate = (dateString, fallback = 0) => {
  if (!dateString) return fallback;

  // Try parsing ISO format or other recognized formats
  let parsedDate = moment(dateString, moment.ISO_8601, true);

  if (!parsedDate.isValid()) {
    // Fallback to parsing "DD.MM.YYYY" format
    parsedDate = moment(dateString, "DD.MM.YYYY", true);
  }
  if (!parsedDate.isValid()) {
    // Fallback to parsing "DD-MM-YYYY" format
    parsedDate = moment(dateString, "DD-MM-YYYY", true);
  }
  if (!parsedDate.isValid()) {
    // Fallback to parsing "DD MMM YYYY" format (e.g., "01 Jan 2020")
    parsedDate = moment(dateString, "DD MMM YYYY", true);
  }

  if (!parsedDate.isValid()) {
    // Handle cases like "Thu Nov 07 2024 00:00:00 GMT+0530"
    const jsDate = new Date(dateString);
    if (!isNaN(jsDate.getTime())) {
      parsedDate = moment(jsDate);
    }
  }

  // Return formatted date in "DD MMM YYYY" format or fallback if invalid
  return parsedDate.isValid() ? parsedDate.format("DD MMM YYYY") : fallback;
};

// Create the PDF component
const OverviewTemplate = ({ schemeOverviewData, investorSchemeDetails, aumDataForSpecificScheme, tableData }) => (
  <View>
    {/* Overview Section */}
    <View style={styles.section}>
      <Text style={styles.heading}>Overview</Text>
      <Text style={styles.subHeading}>{tableData?.scheme_name} - Class {"("}{tableData?.scheme_class}{")"}</Text>
      <Text style={styles.subtitle}>

        {schemeOverviewData?.scheme_description || "Vasuki India Fund Vasuki India Fund is a first closed ended scheme Vasuki India Trust, a CAT III AIF, launched in the Year 2021 with a tenure of five years with an option to extend by two years. Vasuki Hind Capital Private Limited is an Investment Manager of the Fund. Vasuki India Fund offers a well-researched investment approach with a focus on high-quality Indian equities, ensuring a balanced and prudent strategy for long-term wealth growth."}{" "}
      </Text>
    </View>

    <View style={styles.grid}>
      {/* Scheme Tenure */}
      <View style={styles.gridItem}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Scheme Tenure</Text>
          <Text style={styles.cardValue}>{schemeOverviewData?.fund_tenure || 0}</Text>
        </View>
      </View>

      {/* Launch Date */}
      <View style={styles.gridItem}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Launch Date</Text>
          <Text style={styles.cardValue}>
            {!schemeOverviewData?.launch_date ? "N/A" : formatDate(schemeOverviewData?.launch_date) || 0}
          </Text>
        </View>
      </View>

      {/* NAV / Per Unit */}
      <View style={styles.gridItem}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>NAV / Per Unit</Text>
          <Text style={styles.cardValue}><Image src={rupeeSymbol} style={styles.rupeeImage} /> {schemeOverviewData?.nav || 0}</Text>
          <Text style={styles.cardDate}>As of {formatDate(schemeOverviewData?.as_on_date)}</Text>
        </View>
      </View>

      {/* AUM */}
      <View style={styles.gridItem}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>AUM</Text>
          <Text style={styles.cardValue}>
            <Image src={rupeeSymbol} style={styles.rupeeImage} /> {aumDataForSpecificScheme?.aum ? Number((aumDataForSpecificScheme?.aum)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 0}
          </Text>
          <Text style={styles.cardDate}>As of {formatDate(schemeOverviewData?.as_on_date)}</Text>
        </View>
      </View>

      {/* Benchmark */}
      <View style={styles.gridItem}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Benchmark</Text>
          <Text style={styles.cardValue}>{schemeOverviewData?.benchmark || 0}</Text>
        </View>
      </View>
    </View>

    {/* My Portfolio Section */}
    <View style={styles.section}>
      <Text style={styles.heading}>My Portfolio</Text>
    </View>

    <View style={styles.grid}>
      {/* Contribution Amount */}
      <View style={styles.gridItemPortfolio}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Contribution Amount</Text>
          <Text style={styles.cardValue}>
            <Image src={rupeeSymbol} style={styles.rupeeImage} /> {Number(investorSchemeDetails?.contribution).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 0.00}
          </Text>
        </View>
      </View>

      {/* Purchase Price */}
      <View style={styles.gridItemPortfolio}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Purchase Price</Text>
          <Text style={styles.cardValue}>
            <Image src={rupeeSymbol} style={styles.rupeeImage} /> {investorSchemeDetails?.purchase_value ? truncateToDecimalPlaces(investorSchemeDetails?.purchase_value, 2) : 0.00}
          </Text>
        </View>
      </View>

      {/* No. of Units Issued */}
      <View style={styles.gridItemPortfolio}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>No. of Units Issued</Text>
          <Text style={styles.cardValue}>
            {investorSchemeDetails?.number_of_units ? Number(investorSchemeDetails?.number_of_units).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 0.00}
          </Text>
        </View>
      </View>

      {/* Investment Value as of Date */}
      <View style={styles.gridItemPortfolio}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Current Investment Value</Text>
          <Text style={styles.cardValue}>
            <Image src={rupeeSymbol} style={styles.rupeeImage} /> {investorSchemeDetails?.valuation ? Number(investorSchemeDetails?.valuation).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 0.00}
          </Text>
          <Text style={styles.cardDate}>As of {formatDate(schemeOverviewData?.as_on_date)}</Text>
        </View>
      </View>

      <View style={styles.gridItemPortfolio}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Date of Allotement of Units</Text>
          <Text style={styles.cardValue}>
            {investorSchemeDetails?.date_of_allotement_units ? formatDate(investorSchemeDetails?.date_of_allotement_units) : "N/A"}
          </Text>
        </View>
      </View>
    </View>
  </View>
);

export default OverviewTemplate;
